import React, { useState } from "react";
import { gql, useQuery, useMutation } from "@apollo/client";
import { useStore } from "../store";
import "./Onboarding.css";

const GET_PARTNERS = gql`query { partners { id name domain status maxSites isHouse subscriptionUntil } }`;
const GET_SITES    = gql`query { sites { id name partnerId installToken status lat lng } }`;
const GET_USERS    = gql`query { users { id username role partnerId siteId } }`;
const GET_STATS    = gql`query { globalStats { partners sites nodesTotal nodesOnline eventsTotal eventsToday activeSubscriptions } }`;
const GET_HARDWARE = gql`query { hardwareUnits { id uuid type model status partnerId siteId } }`;
const GET_CATALOG  = gql`query { connectorCatalog { id key name description monthlyPrice premium published } }`;
const UPSERT_CAT = gql`mutation($key: String!, $name: String!, $description: String, $monthlyPrice: Int, $premium: Boolean, $published: Boolean) { upsertCatalogConnector(key: $key, name: $name, description: $description, monthlyPrice: $monthlyPrice, premium: $premium, published: $published) { id } }`;
const DELETE_CAT = gql`mutation($id: ID!) { deleteCatalogConnector(id: $id) }`;

const CREATE_PARTNER = gql`
  mutation($name: String!, $domain: String, $companyName: String, $maxSites: Int) {
    createPartner(name: $name, domain: $domain, companyName: $companyName, maxSites: $maxSites) { id }
  }`;
const CREATE_SITE = gql`
  mutation($partnerId: ID, $name: String!, $lat: Float, $lng: Float) {
    createSite(partnerId: $partnerId, name: $name, lat: $lat, lng: $lng) { id name installToken }
  }`;
const UPDATE_SITE = gql`
  mutation($id: ID!, $lat: Float, $lng: Float) {
    updateSite(id: $id, lat: $lat, lng: $lng) { id lat lng }
  }`;
const REGEN_TOKEN = gql`mutation($siteId: ID!) { regenerateSiteToken(siteId: $siteId) { id installToken } }`;
const CREATE_USER = gql`
  mutation($username: String!, $password: String!, $role: Role!, $partnerId: ID, $siteId: ID) {
    createUser(username: $username, password: $password, role: $role, partnerId: $partnerId, siteId: $siteId) { id }
  }`;
const SET_USER_PW = gql`mutation($userId: ID!, $newPassword: String!) { setUserPassword(userId: $userId, newPassword: $newPassword) }`;
const DELETE_USER = gql`mutation($userId: ID!) { deleteUser(userId: $userId) }`;
const SET_PARTNER_STATUS = gql`mutation($partnerId: ID!, $status: String!) { setPartnerStatus(partnerId: $partnerId, status: $status) { id status } }`;
const SET_SUB = gql`mutation($partnerId: ID!, $until: DateTime) { setPartnerSubscription(partnerId: $partnerId, until: $until) { id subscriptionUntil } }`;
const CREATE_HW = gql`mutation($type: HardwareType!, $model: String, $quantity: Int) { createHardware(type: $type, model: $model, quantity: $quantity) { id } }`;
const ASSIGN_HW = gql`mutation($id: ID!, $partnerId: ID!) { assignHardware(id: $id, partnerId: $partnerId) { id } }`;
const ACTIVATE_HW = gql`mutation($uuid: String!, $siteId: ID!) { activateHardware(uuid: $uuid, siteId: $siteId) { id } }`;

export default function Onboarding() {
  const user = useStore(s => s.user);
  const isSuper = user?.role === "SUPERADMIN";

  const statsQ = useQuery(GET_STATS, { skip: !isSuper, pollInterval: 15000 });
  const st = statsQ.data?.globalStats;
  const partnersQ = useQuery(GET_PARTNERS, { skip: !isSuper });
  const sitesQ = useQuery(GET_SITES);
  const usersQ = useQuery(GET_USERS);

  const [createPartner] = useMutation(CREATE_PARTNER, { onCompleted: () => partnersQ.refetch() });
  const [createSite] = useMutation(CREATE_SITE, { onCompleted: () => sitesQ.refetch() });
  const [updateSite] = useMutation(UPDATE_SITE, { onCompleted: () => sitesQ.refetch() });
  const [regenToken] = useMutation(REGEN_TOKEN, { onCompleted: () => sitesQ.refetch() });
  const [createUser] = useMutation(CREATE_USER, { onCompleted: () => usersQ.refetch() });
  const [setUserPw] = useMutation(SET_USER_PW);
  const [deleteUser] = useMutation(DELETE_USER, { onCompleted: () => usersQ.refetch() });
  const [setStatus] = useMutation(SET_PARTNER_STATUS, { onCompleted: () => partnersQ.refetch() });
  const [setSub] = useMutation(SET_SUB, { onCompleted: () => partnersQ.refetch() });
  const hwQ = useQuery(GET_HARDWARE);
  const [createHw] = useMutation(CREATE_HW, { onCompleted: () => hwQ.refetch() });
  const [assignHw] = useMutation(ASSIGN_HW, { onCompleted: () => hwQ.refetch() });
  const [activateHw] = useMutation(ACTIVATE_HW, { onCompleted: () => hwQ.refetch() });
  const catQ = useQuery(GET_CATALOG, { skip: !isSuper });
  const [upsertCat] = useMutation(UPSERT_CAT, { onCompleted: () => catQ.refetch() });
  const [deleteCat] = useMutation(DELETE_CAT, { onCompleted: () => catQ.refetch() });
  const [cKey, setCKey] = useState(""); const [cName, setCName] = useState("");
  const [cDesc, setCDesc] = useState(""); const [cPrice, setCPrice] = useState("0"); const [cPremium, setCPremium] = useState(false);
  const subLabel = (p: any) => {
    if (!p.subscriptionUntil) return "Unlimited";
    const d = new Date(p.subscriptionUntil);
    return d.toLocaleDateString() + (d < new Date() ? " (expired)" : "");
  };
  const plusYear = () => new Date(Date.now() + 365 * 864e5).toISOString();
  const expireNow = () => new Date(Date.now() - 1000).toISOString();

  const [msg, setMsg] = useState<string>("");
  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };
  const run = async (fn: () => Promise<any>, ok: string) => {
    try { await fn(); flash(ok); } catch (e: any) { flash("Error: " + e.message); }
  };

  // forms
  const [pName, setPName] = useState(""); const [pDomain, setPDomain] = useState("");
  const [sName, setSName] = useState(""); const [sPartner, setSPartner] = useState("");
  const [sLat, setSLat] = useState(""); const [sLng, setSLng] = useState("");
  const [loc, setLoc] = useState<Record<string, { lat: string; lng: string }>>({});
  const setLocVal = (id: string, k: "lat" | "lng", v: string) => setLoc(p => ({ ...p, [id]: { ...(p[id] ?? { lat: "", lng: "" }), [k]: v } }));
  const [uName, setUName] = useState(""); const [uPass, setUPass] = useState("");
  const [uRole, setURole] = useState("OPERATOR"); const [uSite, setUSite] = useState(""); const [uPartner, setUPartner] = useState("");
  const [hwType, setHwType] = useState("DETECTION_BOARD"); const [hwModel, setHwModel] = useState(""); const [hwQty, setHwQty] = useState("1");
  const [asgId, setAsgId] = useState(""); const [asgPid, setAsgPid] = useState("");
  const [actUuid, setActUuid] = useState(""); const [actSite, setActSite] = useState("");
  const [provSite, setProvSite] = useState<string>("");

  const copy = (t: string) => { navigator.clipboard?.writeText(t); flash("Copied install token"); };

  const sites = sitesQ.data?.sites || [];
  const partners = partnersQ.data?.partners || [];
  const hw = hwQ.data?.hardwareUnits || [];
  const hwStock = hw.filter((h: any) => h.status === "STOCK");
  const hwAssigned = hw.filter((h: any) => h.status === "ASSIGNED");
  const partnerName = (id: string) => partners.find((p: any) => p.id === id)?.name || (id ? id.slice(0, 8) : "—");
  const siteName = (id: string) => sites.find((s: any) => s.id === id)?.name || (id ? id.slice(0, 8) : "—");

  return (
    <div className="page-container onb">
      <div className="page-header"><h2>⚙ Management</h2></div>
      {msg && <div className={"onb-flash" + (msg.startsWith("Error") ? " err" : "")}>{msg}</div>}

      {isSuper && st && (
        <div className="onb-metrics">
          <div className="onb-metric"><div className="m-val">{st.partners}</div><div className="m-lbl">Partners</div></div>
          <div className="onb-metric"><div className="m-val">{st.sites}</div><div className="m-lbl">Objects</div></div>
          <div className="onb-metric"><div className="m-val">{st.nodesOnline}/{st.nodesTotal}</div><div className="m-lbl">Nodes online</div></div>
          <div className="onb-metric"><div className="m-val">{st.activeSubscriptions}</div><div className="m-lbl">Active subs</div></div>
          <div className="onb-metric"><div className="m-val">{st.eventsToday}</div><div className="m-lbl">Events today</div></div>
          <div className="onb-metric"><div className="m-val">{st.eventsTotal}</div><div className="m-lbl">Events total</div></div>
        </div>
      )}

      {isSuper && (
        <div className="onb-card">
          <h3>Partners</h3>
          <table className="onb-table">
            <thead><tr><th>Name</th><th>Domain</th><th>Status</th><th>Subscription</th><th></th></tr></thead>
            <tbody>
              {partners.map((p: any) => (
                <tr key={p.id}>
                  <td>{p.name}{p.isHouse ? " (house)" : ""}</td>
                  <td>{p.domain || "—"}</td>
                  <td><span className={p.status === "FROZEN" ? "st-frozen" : "st-active"}>{p.status}</span></td>
                  <td>
                    <span className={p.subscriptionUntil && new Date(p.subscriptionUntil) < new Date() ? "st-frozen" : "st-active"}>{subLabel(p)}</span>
                    {!p.isHouse && (
                      <div style={{ marginTop: 4 }}>
                        <button className="onb-btn-sm" onClick={() => run(() => setSub({ variables: { partnerId: p.id, until: plusYear() } }), "Subscription extended +1y")}>+1 year</button>
                        <button className="onb-btn-sm" onClick={() => run(() => setSub({ variables: { partnerId: p.id, until: null } }), "Subscription set unlimited")}>Unlimited</button>
                        <button className="onb-btn-sm" onClick={() => run(() => setSub({ variables: { partnerId: p.id, until: expireNow() } }), "Subscription expired")}>Expire</button>
                      </div>
                    )}
                  </td>
                  <td>
                    {!p.isHouse && (
                      <button className="onb-btn-sm" onClick={() => run(() =>
                        setStatus({ variables: { partnerId: p.id, status: p.status === "FROZEN" ? "ACTIVE" : "FROZEN" } }),
                        "Partner status updated")}>
                        {p.status === "FROZEN" ? "Unfreeze" : "Kill switch"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="onb-form">
            <input placeholder="Partner name" value={pName} onChange={e => setPName(e.target.value)} />
            <input placeholder="Domain (optional)" value={pDomain} onChange={e => setPDomain(e.target.value)} />
            <button onClick={() => run(() => createPartner({ variables: { name: pName, domain: pDomain || null, companyName: pName } }), "Partner created").then(() => { setPName(""); setPDomain(""); })}>
              Create partner
            </button>
          </div>
        </div>
      )}

      <div className="onb-card">
        <h3>Sites (objects)</h3>
        <table className="onb-table">
          <thead><tr><th>Name</th><th>Location (lat, lng)</th><th>Install token</th><th></th></tr></thead>
          <tbody>
            {sites.map((s: any) => (
              <tr key={s.id}>
                <td>{s.name}</td>
                <td>
                  <input style={{ width: 90 }} placeholder="lat" value={loc[s.id]?.lat ?? (s.lat ?? "")} onChange={e => setLocVal(s.id, "lat", e.target.value)} />
                  <input style={{ width: 90, marginLeft: 4 }} placeholder="lng" value={loc[s.id]?.lng ?? (s.lng ?? "")} onChange={e => setLocVal(s.id, "lng", e.target.value)} />
                  <button className="onb-btn-sm" style={{ marginLeft: 6 }} onClick={() => {
                    const v = loc[s.id] ?? { lat: s.lat, lng: s.lng };
                    run(() => updateSite({ variables: { id: s.id, lat: v.lat === "" || v.lat == null ? null : parseFloat(v.lat), lng: v.lng === "" || v.lng == null ? null : parseFloat(v.lng) } }), "Location saved");
                  }}>Save</button>
                </td>
                <td className="onb-token" title={s.installToken}>{s.installToken}</td>
                <td>
                  <button className="onb-btn-sm" onClick={() => copy(s.installToken)}>Copy</button>
                  <button className="onb-btn-sm" onClick={() => run(() => regenToken({ variables: { siteId: s.id } }), "Token regenerated")}>Regenerate</button>
                  <button className="onb-btn-sm" onClick={() => setProvSite(provSite === s.id ? "" : s.id)}>Provision</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="onb-form">
          <input placeholder="Site name (e.g. Warehouse-1)" value={sName} onChange={e => setSName(e.target.value)} />
          <input style={{ width: 90 }} placeholder="lat" value={sLat} onChange={e => setSLat(e.target.value)} />
          <input style={{ width: 90 }} placeholder="lng" value={sLng} onChange={e => setSLng(e.target.value)} />
          {isSuper && (
            <select value={sPartner} onChange={e => setSPartner(e.target.value)}>
              <option value="">— partner —</option>
              {partners.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <button onClick={() => run(() => createSite({ variables: { name: sName, partnerId: isSuper ? (sPartner || null) : null, lat: sLat ? parseFloat(sLat) : null, lng: sLng ? parseFloat(sLng) : null } }), "Site created").then(() => { setSName(""); setSLat(""); setSLng(""); })}>
            Create site
          </button>
        </div>

        {provSite && (() => {
          const s = sites.find((x: any) => x.id === provSite);
          if (!s) return null;
          const origin = window.location.origin;
          const tok = s.installToken;
          const camPs = `irm "${origin}/install-camera-connector.ps1?token=${tok}" | iex`;
          const camSh = `curl -fsSL "${origin}/install-camera-connector.sh?token=${tok}" | bash`;
          const nodeSh = `curl -fsSL "${origin}/install-node.sh?token=${tok}" | bash`;
          const cfgUrl = `${origin}/connector/config?token=${tok}`;
          const batUrl = `${origin}/install-node.bat?token=${tok}`;
          const cmdRow = (label: string, cmd: string) => (
            <div style={{ margin: "6px 0" }}>
              <div style={{ fontSize: 12, color: "#90caf9" }}>{label}</div>
              <code style={{ display: "block", background: "#0d1b2a", padding: "6px 8px", borderRadius: 4, fontSize: 12, wordBreak: "break-all" }}>{cmd}</code>
              <button className="onb-btn-sm" style={{ marginTop: 4 }} onClick={() => { navigator.clipboard?.writeText(cmd); flash("Copied command"); }}>Copy</button>
            </div>
          );
          return (
            <div style={{ marginTop: 12, borderTop: "1px solid #1f3350", paddingTop: 10 }}>
              <h4 style={{ margin: "0 0 6px" }}>Provision “{s.name}” — connector install</h4>
              <p style={{ opacity: 0.7, fontSize: 12, margin: "0 0 8px" }}>Runs in the client's network (outbound HTTPS only). Site token: <code>{tok}</code></p>
              <b style={{ fontSize: 13 }}>📷 Camera Connector (ONVIF / RTSP)</b>
              {cmdRow("Windows (PowerShell + Docker Desktop):", camPs)}
              {cmdRow("Linux (bash + Docker):", camSh)}
              <div style={{ margin: "6px 0" }}><a href={cfgUrl} style={{ color: "#7fd4ff", fontSize: 12 }}>⬇ config.json</a> <span style={{ opacity: 0.6, fontSize: 12 }}>then add cameras (onvif host or rtspUrl, lat/lng, bearingDeg, fovDeg)</span></div>
              <b style={{ fontSize: 13 }}>🖥 Node Agent</b>
              {cmdRow("Linux (bash):", nodeSh)}
              <div style={{ margin: "6px 0" }}><a href={batUrl} style={{ color: "#7fd4ff", fontSize: 12 }}>⬇ install-uav-node.bat (Windows)</a></div>
              <button className="onb-btn-sm" onClick={() => setProvSite("")}>Close</button>
            </div>
          );
        })()}
      </div>

      <div className="onb-card">
        <h3>Users</h3>
        <table className="onb-table">
          <thead><tr><th>Username</th><th>Role</th><th>Site</th><th>Actions</th></tr></thead>
          <tbody>
            {(usersQ.data?.users || []).map((u: any) => (
              <tr key={u.id}>
                <td>{u.username}</td>
                <td>{u.role}</td>
                <td>{sites.find((s: any) => s.id === u.siteId)?.name || "—"}</td>
                <td>
                  <button className="onb-mini" onClick={() => {
                    const pw = window.prompt(`New password for "${u.username}" (min. 8 chars):`);
                    if (pw) run(() => setUserPw({ variables: { userId: u.id, newPassword: pw } }), "Password reset");
                  }}>Reset password</button>
                  {u.id !== user?.id && (
                    <button className="onb-mini danger" onClick={() => {
                      if (window.confirm(`Delete user "${u.username}"? This cannot be undone.`))
                        run(() => deleteUser({ variables: { userId: u.id } }), "User deleted");
                    }}>Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="onb-form">
          <input placeholder="Username" value={uName} onChange={e => setUName(e.target.value)} />
          <input placeholder="Password" type="text" value={uPass} onChange={e => setUPass(e.target.value)} />
          <select value={uRole} onChange={e => setURole(e.target.value)}>
            <option value="OPERATOR">OPERATOR</option>
            <option value="VIEWER">VIEWER</option>
            {isSuper && <option value="PARTNER_ADMIN">PARTNER_ADMIN</option>}
            {isSuper && <option value="PARTNER_DISPATCHER">PARTNER_DISPATCHER</option>}
          </select>
          {(uRole === "OPERATOR" || uRole === "VIEWER") && (
            <select value={uSite} onChange={e => setUSite(e.target.value)}>
              <option value="">— site —</option>
              {sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          )}
          {isSuper && (uRole === "PARTNER_ADMIN" || uRole === "PARTNER_DISPATCHER") && (
            <select value={uPartner} onChange={e => setUPartner(e.target.value)}>
              <option value="">— partner —</option>
              {(partnersQ.data?.partners || []).filter((p: any) => !p.isHouse).map((p: any) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <button onClick={() => run(() => createUser({ variables: {
            username: uName, password: uPass, role: uRole,
            siteId: (uRole === "OPERATOR" || uRole === "VIEWER") ? (uSite || null) : null,
            partnerId: (uRole === "PARTNER_ADMIN" || uRole === "PARTNER_DISPATCHER") ? (uPartner || null) : null,
          } }), "User created").then(() => { setUName(""); setUPass(""); setUPartner(""); })}>
            Create user
          </button>
        </div>
      </div>

      <div className="onb-card">
        <h3>Hardware Registry</h3>
        <table className="onb-table">
          <thead><tr><th>UUID</th><th>Type</th><th>Model</th><th>Status</th><th>Partner</th><th>Object</th></tr></thead>
          <tbody>
            {hw.map((h: any) => (
              <tr key={h.id}>
                <td className="onb-token" title={h.uuid}>{h.uuid}</td>
                <td>{h.type}</td>
                <td>{h.model || "—"}</td>
                <td>{h.status}</td>
                <td>{h.partnerId ? partnerName(h.partnerId) : "—"}</td>
                <td>{h.siteId ? siteName(h.siteId) : "—"}</td>
              </tr>
            ))}
            {hw.length === 0 && <tr><td colSpan={6} style={{ opacity: 0.6 }}>No hardware yet.</td></tr>}
          </tbody>
        </table>

        {isSuper && (
          <div className="onb-form">
            <select value={hwType} onChange={e => setHwType(e.target.value)}>
              {["DETECTION_BOARD", "TURRET", "INTERCEPTOR", "NETGUN", "CAMERA", "SENSOR"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <input placeholder="Model (optional)" list="hw-models" value={hwModel} onChange={e => setHwModel(e.target.value)} />
            <datalist id="hw-models">
              {[...new Set(hw.map((h: any) => h.model).filter(Boolean))].map((m: any) => <option key={m} value={m} />)}
            </datalist>
            <input style={{ width: 70 }} type="number" min={1} value={hwQty} onChange={e => setHwQty(e.target.value)} />
            <button onClick={() => run(() => createHw({ variables: { type: hwType, model: hwModel || null, quantity: parseInt(hwQty) || 1 } }), "Hardware generated").then(() => setHwModel(""))}>
              Generate
            </button>
          </div>
        )}

        {isSuper && (
          <div className="onb-form" style={{ marginTop: 8 }}>
            <select value={asgId} onChange={e => setAsgId(e.target.value)}>
              <option value="">— stock unit —</option>
              {hwStock.map((h: any) => <option key={h.id} value={h.id}>{h.type} · {h.uuid.slice(0, 14)}…</option>)}
            </select>
            <select value={asgPid} onChange={e => setAsgPid(e.target.value)}>
              <option value="">— partner —</option>
              {partners.map((p: any) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button onClick={() => run(() => assignHw({ variables: { id: asgId, partnerId: asgPid } }), "Hardware assigned").then(() => { setAsgId(""); setAsgPid(""); })}>
              Assign to partner
            </button>
          </div>
        )}

        <div className="onb-form" style={{ marginTop: 8 }}>
          <select value={actUuid} onChange={e => setActUuid(e.target.value)}>
            <option value="">— assigned unit —</option>
            {hwAssigned.map((h: any) => <option key={h.id} value={h.uuid}>{h.type} · {h.uuid.slice(0, 14)}…</option>)}
          </select>
          <select value={actSite} onChange={e => setActSite(e.target.value)}>
            <option value="">— object —</option>
            {sites.map((s: any) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <button onClick={() => run(() => activateHw({ variables: { uuid: actUuid, siteId: actSite } }), "Hardware activated").then(() => { setActUuid(""); setActSite(""); })}>
            Activate on object
          </button>
        </div>
      </div>

      {isSuper && (
        <div className="onb-card">
          <h3>Connector Catalog (Integration Hub)</h3>
          <table className="onb-table">
            <thead><tr><th>Key</th><th>Name</th><th>$/mo</th><th>Published</th><th></th></tr></thead>
            <tbody>
              {(catQ.data?.connectorCatalog || []).map((c: any) => (
                <tr key={c.id}>
                  <td className="onb-token">{c.key}</td>
                  <td>{c.name}</td>
                  <td>{c.monthlyPrice}</td>
                  <td><span className={c.published ? "st-active" : "st-frozen"}>{c.published ? "yes" : "no"}</span></td>
                  <td>
                    <button className="onb-btn-sm" onClick={() => run(() => upsertCat({ variables: { key: c.key, name: c.name, description: c.description, monthlyPrice: c.monthlyPrice, premium: c.premium, published: !c.published } }), "Catalog updated")}>
                      {c.published ? "Unpublish" : "Publish"}
                    </button>
                    <button className="onb-btn-sm" onClick={() => run(() => deleteCat({ variables: { id: c.id } }), "Connector removed")}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="onb-form">
            <input style={{ width: 110 }} placeholder="KEY" value={cKey} onChange={e => setCKey(e.target.value.toUpperCase())} />
            <input placeholder="Name" value={cName} onChange={e => setCName(e.target.value)} />
            <input placeholder="Description" value={cDesc} onChange={e => setCDesc(e.target.value)} />
            <input style={{ width: 80 }} type="number" placeholder="$/mo" value={cPrice} onChange={e => setCPrice(e.target.value)} />
            <label style={{ color: "#90caf9", fontSize: 13 }}><input type="checkbox" checked={cPremium} onChange={e => setCPremium(e.target.checked)} /> premium</label>
            <button onClick={() => run(() => upsertCat({ variables: { key: cKey, name: cName, description: cDesc || null, monthlyPrice: parseInt(cPrice) || 0, premium: cPremium, published: true } }), "Connector saved").then(() => { setCKey(""); setCName(""); setCDesc(""); setCPrice("0"); setCPremium(false); })}>
              Add / Update
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
